import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      api.post("/subscriptions/sync").then(() => refresh()).catch((e) => console.error("Sub sync failed:", e));
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async () => {
    setErr("");
    if (!user) { nav("/login", { state: { from: "/pricing" }}); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e));
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setErr("");
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscriptions/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e));
      setPortalLoading(false);
    }
  };

  const features = [
    "3 swing video uploads per month",
    "Written teardown from your coach on every clip",
    "Direct comment threads — pinned coach replies in gold",
    "Access to the full Dr. Cuts swing feed",
    "Cancel anytime — no annual lock-in",
  ];

  return (
    <div data-testid="pricing-page" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="Membership — $10/mo" description="Become a Cut Hunter member for $10/month. Unlimited swing uploads, written coach teardowns on every clip, and full access to the Dr. Cuts swing feed." path="/pricing" />
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <div className="overline">Membership</div>
          <h1 className="font-display text-5xl sm:text-7xl mt-4"><span className="italic text-[#C9A227]">One shot shape.</span></h1>
          <p className="text-white/65 mt-6 max-w-xl mx-auto">Everything you need to actually hit cuts on demand.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Free tier */}
          <div className="p-10 border border-white/10 bg-white/[0.02]" data-testid="plan-free">
            <div className="overline" style={{color:"#A0B0A8"}}>Spectator</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-6xl">$0</span>
              <span className="text-white/50 text-sm">/forever</span>
            </div>
            <p className="text-white/60 text-sm mt-4">Browse the swing feed, read the coaching threads, learn the method.</p>
            <ul className="mt-8 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3"><span className="text-[#C9A227] mt-1">→</span>View all member swings</li>
              <li className="flex items-start gap-3"><span className="text-[#C9A227] mt-1">→</span>Read public coaching threads</li>
              <li className="flex items-start gap-3"><span className="text-white/30 mt-1">×</span><span className="text-white/40">No video uploads</span></li>
            </ul>
            <Link to="/feed" className="btn-ghost mt-10 inline-block">Browse Feed</Link>
          </div>

          {/* Pro tier */}
          <div className="p-10 border-2 border-[#C9A227] bg-[#0c1612] relative" data-testid="plan-pro">
            <div className="absolute -top-3 left-10 bg-[#C9A227] text-[#050A08] text-[10px] font-bold tracking-[0.2em] px-3 py-1 uppercase">Members Only</div>
            <div className="overline">Cut Hunter</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-6xl text-[#C9A227]">$10</span>
              <span className="text-white/50 text-sm">/month</span>
            </div>
            <p className="text-white/70 text-sm mt-4">Everything you need to hit a high-holding fade on command.</p>
            <ul className="mt-8 space-y-3 text-sm text-white/85">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3"><span className="text-[#C9A227] mt-1">✓</span>{f}</li>
              ))}
            </ul>
            {user?.is_pro ? (
              <div className="mt-10 space-y-3">
                <div data-testid="pro-active-badge" className="p-4 border border-[#C9A227]/40 bg-[#C9A227]/5 text-center text-[#C9A227] text-sm font-semibold tracking-wider uppercase">
                  Membership Active{user.subscription_status === "canceled" ? " · Ending" : ""}
                </div>
                <button data-testid="manage-membership-btn" disabled={portalLoading} onClick={openPortal} className="btn-ghost w-full">
                  {portalLoading ? "Opening..." : "Manage Membership"}
                </button>
              </div>
            ) : (
              <button data-testid="pricing-checkout-btn" disabled={loading} onClick={checkout} className="btn-primary w-full mt-10">
                {loading ? "Redirecting..." : "Start Membership"}
              </button>
            )}
            {err && <div className="text-red-400 text-sm mt-4" data-testid="pricing-error">{err}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
